import { JsonValue } from "@common/types/json";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    addTermPairingIssues,
    refineScheduleOverrideComponents,
    refineDailyProRataTermUnit,
    scheduleRecognitionComponentsInputSchema,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    ScheduleChangeJournalEntryMode,
    ScheduleDeleteJournalEntryMode,
    ScheduleLifecycleErrorCode,
    ScheduleLifecycleTreatment,
    ScheduleLifecycleVerb,
    ScheduleStopSettlementKind,
} from "@common/types/domains/accounting/schedule";
import {
    ScheduleSourcePreviousCodingKind,
    expenseSubModeSchema,
    revenueSubModeSchema,
    scheduleTypeSchema,
    scheduleJeSubtypeSchema,
    scheduleLifecycleVerbSchema,
    scheduleSourceTypeSchema,
    scheduleTermUnitSchema,
} from "./schedule.schema";

import { scheduleApplicationListItemSchema } from "./scheduleApplicationList.schema";
import { scheduleLinkedSourceConflictErrorSchema } from "./scheduleLinkedSource.schema";

function parseJsonValue(value: string): JsonValue {
    return JSON.parse(value);
}

/** Returned (400) when a Change would leave the schedule exactly as it is. */
export const SCHEDULE_NOTHING_TO_CHANGE_MESSAGE = "Nothing to change.";

/**
 * Returned (409) when any journal entry an action would write, reverse or
 * delete falls on or before the entity's lock date. Lifecycle actions are
 * all-or-nothing: nothing is written, and the blocked months are named so the
 * user can unlock them rather than being told only that "a period is locked".
 */
export const scheduleLifecyclePeriodLockedErrorSchema = z.object({
    errorCode: z.literal(ScheduleLifecycleErrorCode.periodLocked),
    message: z.string(),
    lockDate: z.string(),
    lockedMonths: z.array(z.string()),
    /**
     * `YYYY-MM` keys of the locked recognition months, set only when an
     * accrued invoice or payment schedule starts in a locked period.
     */
    accruedStartLockedMonths: z.array(z.string()).optional(),
});

export type ScheduleLifecyclePeriodLockedError = z.infer<
    typeof scheduleLifecyclePeriodLockedErrorSchema
>;

/**
 * 409 body for schedule actions. The structured shapes come first so the
 * richer payload wins: `ErrorMessageSchema` is a plain object, and Zod strips
 * unknown keys, so matching it first would silently drop `lockedMonths`.
 */
export const scheduleConflictResponseSchema = z.union([
    scheduleLifecyclePeriodLockedErrorSchema,
    scheduleLinkedSourceConflictErrorSchema,
    ErrorMessageSchema,
]);

export const scheduleLifecycleTreatmentSchema = z.nativeEnum(
    ScheduleLifecycleTreatment,
);

export const scheduleLifecycleProposalJeSchema = z.object({
    date: z.string(),
    description: z.string(),
    amount: z.number(),
    subtype: scheduleJeSubtypeSchema,
    debitAccountId: z.string().uuid(),
    creditAccountId: z.string().uuid(),
    periodId: z.string().uuid().nullable(),
    /** Reversals: the period row whose journal entry this one cancels. */
    reversesPeriodId: z.string().uuid().nullable(),
    /**
     * Reversals of a legacy period-less entry: the cancelled pair's reference,
     * the one thing that tells two such entries on one date apart.
     */
    reversesJournalEntryReference: z.string().nullable().optional(),
});

export type ScheduleLifecycleProposalJe = z.infer<
    typeof scheduleLifecycleProposalJeSchema
>;

export const scheduleLifecycleSourceRestorationSchema = z.object({
    kind: z.nativeEnum(ScheduleSourcePreviousCodingKind),
    isBestEffort: z.boolean(),
});

export const scheduleChangeJournalEntryModeSchema = z.nativeEnum(
    ScheduleChangeJournalEntryMode,
);

/**
 * Change only: which ways of correcting the earned entries the confirm may
 * offer. Reversing or removing touches every earned month, so any locked one
 * leaves the catch-up as the only mode; `lockedMonths` names them for the
 * unlock hint.
 */
export const scheduleChangeOptionsSchema = z.object({
    availableJournalEntryModes: z.array(scheduleChangeJournalEntryModeSchema),
    defaultJournalEntryMode: scheduleChangeJournalEntryModeSchema,
    earnedJournalEntryCount: z.number().int(),
    lockedMonths: z.array(z.string()),
    catchUpDate: z.string(),
    /** Signed: positive recognizes more, negative gives some back. */
    catchUpAmount: z.number(),
});

export type ScheduleChangeOptions = z.infer<typeof scheduleChangeOptionsSchema>;

export const scheduleLifecycleProposalSummarySchema = z.object({
    templateName: z.string(),
    sourceType: scheduleSourceTypeSchema,
    sourceLabel: z.string(),
    isMilestone: z.boolean(),
    holdingAccountName: z.string().nullable(),
    holdingAccountId: z.string().uuid().nullable(),
    recognitionAccountName: z.string().nullable(),
    /** Which account types count as holding, so a reclass can exclude them. */
    scheduleType: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullable(),
    expenseSubMode: expenseSubModeSchema.nullable(),
    postedCount: z.number().int(),
    recognizedAmount: z.number(),
    remainingAmount: z.number(),
    /**
     * Stop only: the holding balance once the stop has voided every period
     * from tomorrow on. `remainingAmount` still counts those future posted
     * periods as recognized.
     */
    remainingAfterStopAmount: z.number().nullable(),
    reversedJournalEntryCount: z.number().int(),
    deletedJournalEntryCount: z.number().int(),
    createdJournalEntryCount: z.number().int(),
    newPeriodAmount: z.number().nullable(),
    previousAmount: z.number().nullable(),
    newAmount: z.number().nullable(),
    /** Resume: the date of the last entry still on the ledger, the default pick. */
    lastJournalEntryDate: z.string().nullable(),
    changeOptions: scheduleChangeOptionsSchema.nullable(),
});

export const scheduleLifecycleProposalSchema = z.object({
    applicationId: z.string().uuid(),
    verb: scheduleLifecycleVerbSchema,
    effectiveDate: z.string(),
    treatment: scheduleLifecycleTreatmentSchema,
    releaseAccountId: z.string().uuid().nullable(),
    voidPeriodIds: z.array(z.string().uuid()),
    jes: z.array(scheduleLifecycleProposalJeSchema),
    sourceRestoration: scheduleLifecycleSourceRestorationSchema.nullable(),
    summary: scheduleLifecycleProposalSummarySchema,
});

export type ScheduleLifecycleProposal = z.infer<
    typeof scheduleLifecycleProposalSchema
>;

export type ScheduleLifecycleProposalQuery = z.infer<
    typeof scheduleLifecycleProposalQuerySchema
>;

export const scheduleLifecycleActionBodySchema = z.object({
    entityId: z.number().int().positive(),
    /**
     * Required by pause, resume and cancel: the date the change takes effect.
     * Entries dated on or after it are removed (pause/cancel) or written
     * (resume); earned entries are never touched either way.
     */
    effectiveDate: z.string().date().optional(),
});

const RECLASS_ACCOUNT_MESSAGE =
    "Reclassify needs an account; accelerate must not have one";

function addSettlementIssues(
    ctx: z.RefinementCtx,
    settlement: {
        kind?: ScheduleStopSettlementKind;
        releaseAccountId?: string;
    },
): void {
    const isReclass = settlement.kind === ScheduleStopSettlementKind.reclass;
    if (isReclass !== (settlement.releaseAccountId !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["releaseAccountId"],
            message: RECLASS_ACCOUNT_MESSAGE,
        });
    }
}

export const scheduleStopSettlementKindSchema = z.nativeEnum(
    ScheduleStopSettlementKind,
);

export const scheduleStopSettlementSchema = z
    .object({
        kind: scheduleStopSettlementKindSchema,
        releaseAccountId: z.string().uuid().optional(),
    })
    .superRefine((settlement, ctx) => addSettlementIssues(ctx, settlement));

export type ScheduleStopSettlement = z.infer<
    typeof scheduleStopSettlementSchema
>;

export type ScheduleLifecycleActionBody = z.infer<
    typeof scheduleLifecycleActionBodySchema
>;

/** Stop can settle what is left on the holding account in the same action. */
export const scheduleLifecycleCancelBodySchema =
    scheduleLifecycleActionBodySchema.extend({
        settlement: scheduleStopSettlementSchema.optional(),
    });

export type ScheduleLifecycleCancelBody = z.infer<
    typeof scheduleLifecycleCancelBodySchema
>;

export const scheduleDeleteJournalEntryModeSchema = z.nativeEnum(
    ScheduleDeleteJournalEntryMode,
);

export const scheduleLifecycleDeleteBodySchema =
    scheduleLifecycleActionBodySchema.extend({
        /** Defaults to `reverse`, the history-preserving option. */
        journalEntryMode: scheduleDeleteJournalEntryModeSchema.optional(),
    });

export type ScheduleLifecycleDeleteBody = z.infer<
    typeof scheduleLifecycleDeleteBodySchema
>;

export const scheduleLifecycleChangeSchema = z
    .object({
        amount: z.number().positive().optional(),
        termLength: z.number().int().positive().optional(),
        termUnit: scheduleTermUnitSchema.optional(),
        termStart: z.string().date().optional(),
        /** Door 1: re-price an invoiced schedule from the invoice's current amount. */
        shouldSyncAmountToSource: z.boolean().optional(),
        /** New rows or recognition account turn the schedule into a one-off. */
        components: scheduleRecognitionComponentsInputSchema.optional(),
        recognitionAccountId: z.string().uuid().optional(),
    })
    .superRefine((change, ctx) => {
        addTermPairingIssues(ctx, change);
        if (change.components) {
            refineScheduleOverrideComponents({
                components: change.components,
                ctx,
            });
        }
        refineDailyProRataTermUnit(change, ctx);
    });

export type ScheduleLifecycleChange = z.infer<
    typeof scheduleLifecycleChangeSchema
>;

export const scheduleLifecycleProposalQuerySchema = z
    .object({
        entityId: z.coerce.number().int().positive(),
        verb: scheduleLifecycleVerbSchema,
        effectiveDate: z.string().date().optional(),
        changeAmount: z.coerce.number().positive().optional(),
        changeTermLength: z.coerce.number().int().positive().optional(),
        changeTermUnit: scheduleTermUnitSchema.optional(),
        changeTermStart: z.string().date().optional(),
        changeShouldSyncAmountToSource: z
            .enum(["true", "false"])
            .optional()
            .transform((value) => value === "true"),
        /** JSON-encoded rows, as a query string cannot carry the array. */
        changeComponents: z
            .string()
            .transform((value, ctx) => {
                try {
                    return parseJsonValue(value);
                } catch {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "changeComponents must be JSON",
                    });
                    return z.NEVER;
                }
            })
            .pipe(scheduleRecognitionComponentsInputSchema)
            .optional(),
        changeRecognitionAccountId: z.string().uuid().optional(),
        /** Stop only: preview the entry that settles the remaining balance. */
        settlementKind: scheduleStopSettlementKindSchema.optional(),
        settlementReleaseAccountId: z.string().uuid().optional(),
    })
    .superRefine((query, ctx) => {
        addTermPairingIssues(ctx, {
            termLength: query.changeTermLength,
            termUnit: query.changeTermUnit,
        });
        if (query.changeComponents) {
            refineScheduleOverrideComponents({
                components: query.changeComponents,
                ctx,
                path: ["changeComponents"],
            });
        }
        refineDailyProRataTermUnit(
            {
                components: query.changeComponents,
                termUnit: query.changeTermUnit,
                termUnitPath: "changeTermUnit",
            },
            ctx,
        );
        if (
            query.settlementKind === undefined &&
            query.settlementReleaseAccountId === undefined
        ) {
            return;
        }
        if (query.verb !== ScheduleLifecycleVerb.cancel) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["settlementKind"],
                message: "Only a stop can settle the remaining balance",
            });
        }
        if (query.settlementKind === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["settlementKind"],
                message: "A settlement account needs a settlement kind",
            });
            return;
        }
        addSettlementIssues(ctx, {
            kind: query.settlementKind,
            releaseAccountId: query.settlementReleaseAccountId,
        });
    });

export const scheduleLifecycleChangeBodySchema =
    scheduleLifecycleActionBodySchema.extend({
        /** Defaults to `reverse`, the history-preserving option. */
        journalEntryMode: scheduleChangeJournalEntryModeSchema.optional(),
        change: scheduleLifecycleChangeSchema.optional(),
    });

export type ScheduleLifecycleChangeBody = z.infer<
    typeof scheduleLifecycleChangeBodySchema
>;

export const scheduleLifecycleActionResponseSchema = z.object({
    application: scheduleApplicationListItemSchema,
});

/**
 * Delete has one shape for both outcomes: `application` carries the retired row
 * when anything had posted (its entries were reversed, so the row survives to
 * own them) and is null when the application was removed outright.
 */
export const scheduleDeleteResponseSchema = z.object({
    id: z.string().uuid(),
    application: scheduleApplicationListItemSchema.nullable(),
});

export type ScheduleLifecycleActionResponse = z.infer<
    typeof scheduleLifecycleActionResponseSchema
>;
